import { defineNuxtPlugin } from '#app'

const RELOAD_KEY = 'chunk-error-reload'
const RELOAD_COOLDOWN_MS = 10_000

function hasRecentReload (): boolean {
  const lastReload = sessionStorage.getItem(RELOAD_KEY)
  if (!lastReload) {
    return false
  }
  return Date.now() - Number(lastReload) < RELOAD_COOLDOWN_MS
}

function markReload (): void {
  sessionStorage.setItem(RELOAD_KEY, String(Date.now()))
}

function isChunkError (error: unknown): boolean {
  if (!(error instanceof Error)) {
    return false
  }
  const msg = error.message
  return (
    msg.includes('Failed to fetch dynamically imported module') ||
    msg.includes('Importing a module script failed') ||
    msg.includes('error loading dynamically imported module') ||
    msg.includes('Loading chunk') ||
    error.name === 'ChunkLoadError'
  )
}

function reloadOnChunkError (error: unknown, context: string): void {
  if (import.meta.dev) {
    console.error(`[chunk-error-recovery] ${context}`, error)
    return
  }

  if (hasRecentReload()) {
    return
  }

  markReload()
  window.location.reload()
}

export default defineNuxtPlugin((nuxtApp: any) => {
  if (import.meta.server || import.meta.dev) {
    return
  }

  window.addEventListener('vite:preloadError', (event) => {
    if (import.meta.dev) {
      console.error('[chunk-error-recovery] vite:preloadError', event)
      return
    }

    if (hasRecentReload()) {
      return
    }
    event.preventDefault()
    markReload()
    window.location.reload()
  })

  nuxtApp.hook('app:chunkError', (error: unknown) => {
    reloadOnChunkError(error, 'app:chunkError')
  })

  nuxtApp.hook('vue:error', (error: unknown) => {
    if (!isChunkError(error)) {
      return
    }
    reloadOnChunkError(error, 'vue:error')
  })

  window.addEventListener('unhandledrejection', (event) => {
    if (!isChunkError(event.reason)) {
      return
    }
    if (import.meta.dev) {
      console.error('[chunk-error-recovery] unhandledrejection', event.reason)
      event.preventDefault()
      return
    }
    if (hasRecentReload()) {
      return
    }
    event.preventDefault()
    markReload()
    window.location.reload()
  })
})
